import { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  analizarSumasSaldos, importarSumasSaldos, getEjercicio,
  analizarDiarioHolded, importarDiarioHolded,
} from "@/lib/api";
import { notifyDataChanged } from "@/lib/events";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Upload, FileSpreadsheet, CheckCircle2, AlertCircle, ArrowLeft, ArrowRight, Wand2, Layers, Plug,
  BookOpen,
} from "lucide-react";

const MODOS = [
  { value: "saldo", label: "Saldo", cols: ["col_saldo"], titulos: ["Columna de Saldo"] },
  { value: "debe_haber", label: "Debe / Haber", cols: ["col_debe", "col_haber"], titulos: ["Columna Debe", "Columna Haber"] },
  { value: "deudor_acreedor", label: "Deudor / Acreedor", cols: ["col_deudor", "col_acreedor"], titulos: ["Columna Deudor", "Columna Acreedor"] },
];

const fmtEur = (n) =>
  typeof n === "number"
    ? n.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €"
    : "—";

function initMapping(preview) {
  const d = preview.deteccion || {};
  const cols = preview.columnas || [];
  return {
    fila_encabezado: preview.encabezado?.fila_encabezado ?? 0,
    col_codigo: d.col_codigo || cols[0] || "",
    col_nombre: d.col_nombre || cols[1] || "",
    modo_saldo: d.modo_saldo || "saldo",
    col_saldo: d.col_saldo || "",
    col_debe: d.col_debe || "",
    col_haber: d.col_haber || "",
    col_deudor: d.col_deudor || "",
    col_acreedor: d.col_acreedor || "",
    excluir_subtotales: preview.excluir_subtotales ?? true,
    excluir_codigos: [],
  };
}

function cleanMapping(m) {
  const base = {
    fila_encabezado: m.fila_encabezado,
    col_codigo: m.col_codigo,
    col_nombre: m.col_nombre,
    modo_saldo: m.modo_saldo,
    excluir_subtotales: m.excluir_subtotales,
    excluir_codigos: m.excluir_codigos || [],
  };
  const modo = MODOS.find((x) => x.value === m.modo_saldo);
  (modo?.cols || []).forEach((c) => { base[c] = m[c]; });
  return base;
}

function mappingValido(m) {
  if (!m.col_codigo || !m.col_nombre || !m.modo_saldo) return false;
  const modo = MODOS.find((x) => x.value === m.modo_saldo);
  return (modo?.cols || []).every((c) => !!m[c]);
}

// Etiqueta legible de una fila cruda para el selector de encabezado.
function filaLabel(celdas, idx) {
  const txt = (celdas || []).map((c) => String(c ?? "").trim()).filter(Boolean).join(" · ");
  return `Fila ${idx + 1}: ${txt.slice(0, 70) || "(vacía)"}`;
}

export default function ImportarPage() {
  const { ejercicioId } = useParams();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const [step, setStep] = useState(1); // 1=subir · 2=validar · 3=resultado
  const [analyzing, setAnalyzing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [result, setResult] = useState(null);

  // Modo alternativo: importar el LIBRO DIARIO de Holded (Excel). Misma tubería
  // que la conexión por API: excluye el cierre y activa el libro navegable.
  const [modoDiario, setModoDiario] = useState(false);
  const [diarioPreview, setDiarioPreview] = useState(null);
  const [diarioResult, setDiarioResult] = useState(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) {
      if (/\.(xlsx|xls|csv)$/i.test(f.name)) setFile(f);
      else toast.error("Solo se aceptan archivos Excel (.xlsx, .xls) o CSV");
    }
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) setFile(e.target.files[0]);
  };

  // Paso 1 → 2: analizar (detección automática)
  const handleAnalizar = async () => {
    if (!file) { toast.error("Selecciona un archivo"); return; }
    setAnalyzing(true);
    try {
      const { data } = await analizarSumasSaldos(ejercicioId, file);
      setPreview(data);
      setMapping(initMapping(data));
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo analizar el archivo");
    } finally {
      setAnalyzing(false);
    }
  };

  // Re-validar con la elección del usuario (recalcula muestra + integridad)
  const handleRevalidar = async (m = mapping, { silent = false } = {}) => {
    if (!mappingValido(m)) { if (!silent) toast.error("Completa la asignación de columnas"); return; }
    setAnalyzing(true);
    try {
      const { data } = await analizarSumasSaldos(ejercicioId, file, cleanMapping(m));
      setPreview(data);
      if (!silent) toast.success("Previsualización actualizada");
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo revalidar");
    } finally {
      setAnalyzing(false);
    }
  };

  // Cambiar la fila de encabezado → re-analizar con esa fila (re-detecta columnas)
  const handleHeaderChange = async (idxStr) => {
    const idx = parseInt(idxStr, 10);
    setAnalyzing(true);
    try {
      const { data } = await analizarSumasSaldos(ejercicioId, file, { fila_encabezado: idx });
      setPreview(data);
      setMapping((m) => ({
        ...initMapping(data),
        excluir_subtotales: m?.excluir_subtotales ?? true,
        excluir_codigos: [],
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo cambiar la fila de encabezado");
    } finally {
      setAnalyzing(false);
    }
  };

  // Toggle "importar solo cuentas de detalle" (excluir subtotales) → revalida
  const handleToggleSubtotales = (checked) => {
    const next = { ...mapping, excluir_subtotales: !!checked };
    setMapping(next);
    handleRevalidar(next, { silent: true });
  };

  // Excluir / incluir manualmente una cuenta concreta → revalida
  const handleToggleExcluirCodigo = (codigo, excluir) => {
    const set = new Set(mapping.excluir_codigos || []);
    if (excluir) set.add(codigo); else set.delete(codigo);
    const next = { ...mapping, excluir_codigos: [...set] };
    setMapping(next);
    handleRevalidar(next, { silent: true });
  };

  // Paso 2 → 3: importar de verdad con el mapping confirmado
  const handleImport = async () => {
    if (!mappingValido(mapping)) { toast.error("Completa la asignación de columnas"); return; }
    setImporting(true);
    try {
      const { data } = await importarSumasSaldos(ejercicioId, file, cleanMapping(mapping));
      setResult(data);
      setStep(3);
      toast.success("Archivo importado correctamente");
      // El sidebar muestra los ejercicios con datos; refrescarlo tras importar.
      notifyDataChanged("ejercicios");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al importar archivo");
    } finally {
      setImporting(false);
    }
  };

  // Diario Holded: paso 1 → 2 (previsualizar, no guarda nada)
  const handleAnalizarDiario = async () => {
    if (!file) { toast.error("Selecciona un archivo"); return; }
    setAnalyzing(true);
    try {
      const { data } = await analizarDiarioHolded(ejercicioId, file);
      setDiarioPreview(data);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo analizar el Libro diario");
    } finally {
      setAnalyzing(false);
    }
  };

  // Diario Holded: paso 2 → 3 (importar de verdad)
  const handleImportDiario = async () => {
    setImporting(true);
    try {
      const { data } = await importarDiarioHolded(ejercicioId, file);
      setDiarioResult(data);
      setStep(3);
      toast.success("Libro diario importado correctamente");
      notifyDataChanged("ejercicios");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al importar el Libro diario");
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setFile(null); setPreview(null); setMapping(null); setResult(null);
    setDiarioPreview(null); setDiarioResult(null); setStep(1);
  };

  const handleContinue = async () => {
    try {
      const { data: ej } = await getEjercicio(ejercicioId);
      if (ej.cuentas_pendientes > 0 || ej.cuentas_error > 0) {
        navigate(`/ejercicios/${ejercicioId}/revision`);
      } else {
        navigate(`/ejercicios/${ejercicioId}/dashboard`);
      }
    } catch {
      navigate(`/ejercicios/${ejercicioId}/dashboard`);
    }
  };

  const goBack = async () => {
    try {
      const { data } = await getEjercicio(ejercicioId);
      navigate(`/empresas/${data.empresa_id}`);
    } catch {
      navigate("/empresas");
    }
  };

  // Acceso a la importación desde Finoview (resuelve la empresa del ejercicio actual)
  const goFinoview = async () => {
    try {
      const { data } = await getEjercicio(ejercicioId);
      navigate(`/empresas/${data.empresa_id}/finoview`);
    } catch {
      toast.error("No se pudo abrir la conexión con Finoview");
    }
  };

  const setM = (patch) => setMapping((m) => ({ ...m, ...patch }));
  const columnas = preview?.columnas || [];
  const modoActual = MODOS.find((x) => x.value === mapping?.modo_saldo);
  const jq = preview?.jerarquia;
  const filasCrudas = preview?.preview_filas?.filas || [];
  const excluidos = new Set(mapping?.excluir_codigos || []);

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={goBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="page-title">Importar Sumas y Saldos</h1>
          <p className="text-muted-foreground mt-1">
            Subir → revisar la detección → importar. La app detecta sola; tú validas o corriges.
          </p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 text-sm">
        {["Subir archivo", "Revisar y validar", "Resultado"].map((label, i) => {
          const n = i + 1;
          const active = step === n;
          const done = step > n;
          return (
            <div key={label} className="flex items-center gap-2">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                active ? "bg-primary text-primary-foreground" : done ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400" : "bg-muted text-muted-foreground"
              }`}>
                <span className="font-mono font-bold">{done ? "✓" : n}</span>
                <span className="font-medium">{label}</span>
              </div>
              {n < 3 && <span className="text-muted-foreground">→</span>}
            </div>
          );
        })}
      </div>

      {/* ───────────────────────── PASO 1: SUBIR ───────────────────────── */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>{modoDiario ? "Libro diario de Holded (Excel)" : "Archivo de Sumas y Saldos"}</CardTitle>
            <CardDescription>
              {modoDiario ? (
                <>Sube el export "Libro diario" de Holded del año de este ejercicio (Contabilidad →
                Libro diario → Exportar Excel). El asiento de cierre se excluye automáticamente
                (igual que la conexión por API) y podrás navegar la contabilidad: sumas y saldos,
                mayores y asientos.</>
              ) : (
                <>Debe contener una columna de código de cuenta, una de descripción y el importe
                (Saldo, o Debe/Haber, o Saldo Deudor/Acreedor). No hace falta limpiarlo ni
                convertirlo a tabla: el asistente detecta el encabezado y descarta los subtotales.</>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              data-testid="dropzone"
            >
              <input
                type="file" id="file-upload" className="hidden"
                accept=".xlsx,.xls,.csv" onChange={handleFileChange} data-testid="file-input"
              />
              <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center">
                {file ? (
                  <>
                    <FileSpreadsheet className="h-12 w-12 text-primary mb-4" />
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-muted-foreground mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                  </>
                ) : (
                  <>
                    <Upload className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="font-medium">Arrastra tu archivo aquí</p>
                    <p className="text-sm text-muted-foreground mt-1">o haz clic para seleccionar</p>
                    <p className="text-xs text-muted-foreground mt-2">Formatos: .xlsx, .xls, .csv</p>
                  </>
                )}
              </label>
            </div>

            {file && (
              <Button
                className="w-full"
                onClick={modoDiario ? handleAnalizarDiario : handleAnalizar}
                disabled={analyzing}
                data-testid={modoDiario ? "analizar-diario-btn" : "analizar-btn"}
              >
                {analyzing ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-2 border-background border-t-transparent mr-2" />Analizando...</>
                ) : modoDiario ? (
                  <><BookOpen className="h-4 w-4 mr-2" />Analizar Libro diario</>
                ) : (
                  <><Wand2 className="h-4 w-4 mr-2" />Analizar archivo</>
                )}
              </Button>
            )}

            {/* Alternativas de origen */}
            <div className="flex flex-wrap items-center justify-center gap-x-2 pt-2 text-sm">
              <Button
                variant="link"
                className="px-1.5 h-auto"
                onClick={() => setModoDiario((v) => !v)}
                data-testid="toggle-modo-diario"
              >
                <BookOpen className="h-3.5 w-3.5 mr-1" />
                {modoDiario
                  ? "…o importar un Sumas y Saldos normal"
                  : "…o importar el diario de Holded (Excel)"}
              </Button>
              <span className="text-muted-foreground">·</span>
              <Button
                variant="link"
                className="px-1.5 h-auto"
                onClick={goFinoview}
                data-testid="finoview-link-importar"
              >
                <Plug className="h-3.5 w-3.5 mr-1" />
                …o conectar desde Finoview
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─────────────── PASO 2 (modo diario Holded): REVISAR ─────────────── */}
      {step === 2 && modoDiario && diarioPreview && (
        <Card data-testid="validar-diario-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              Libro diario de {diarioPreview.anio}
            </CardTitle>
            <CardDescription>
              Así quedará el Sumas y Saldos reconstruido desde el diario (el asiento de
              cierre ya está excluido). Nada se guarda hasta que pulses Importar.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="rounded-md border p-3">
                <p className="text-muted-foreground text-xs">Asientos</p>
                <p className="font-mono font-semibold">{diarioPreview.n_asientos.toLocaleString("es-ES")}</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-muted-foreground text-xs">Líneas</p>
                <p className="font-mono font-semibold">{diarioPreview.n_lineas.toLocaleString("es-ES")}</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-muted-foreground text-xs">De cierre (excluidas)</p>
                <p className="font-mono font-semibold">{diarioPreview.lineas_cierre_excluidas.toLocaleString("es-ES")}</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-muted-foreground text-xs">Cuentas resultantes</p>
                <p className="font-mono font-semibold">{diarioPreview.n_cuentas.toLocaleString("es-ES")}</p>
              </div>
            </div>

            {diarioPreview.cuadrado ? (
              <Alert className="border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <AlertTitle className="text-emerald-700 dark:text-emerald-400">El Sumas y Saldos reconstruido cuadra a cero</AlertTitle>
                <AlertDescription>
                  Suma de saldos: <strong>{fmtEur(diarioPreview.suma_saldos)}</strong> — apertura
                  incluida y cierre excluido, como debe ser.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/40">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-700 dark:text-amber-400">El Sumas y Saldos reconstruido no cuadra a cero</AlertTitle>
                <AlertDescription>
                  Suma de saldos: <strong>{fmtEur(diarioPreview.suma_saldos)}</strong>. Puede faltar
                  el asiento de apertura o el diario del año está incompleto.
                </AlertDescription>
              </Alert>
            )}

            {(diarioPreview.avisos || []).length > 0 && (
              <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/40">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-700 dark:text-amber-400">Avisos</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc pl-4 space-y-1">
                    {diarioPreview.avisos.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label>Muestra de las primeras cuentas (así se importarán)</Label>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="w-[130px]">Código</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead className="text-right w-[140px]">Saldo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(diarioPreview.muestra || []).map((m) => (
                      <TableRow key={m.codigo}>
                        <TableCell className="font-mono text-xs">{m.codigo}</TableCell>
                        <TableCell className="text-xs max-w-[300px] truncate">{m.nombre}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-xs">{fmtEur(m.saldo)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={reset}>Cambiar archivo</Button>
              <Button className="flex-1" onClick={handleImportDiario} disabled={importing} data-testid="importar-diario-btn">
                {importing ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-2 border-background border-t-transparent mr-2" />Importando...</>
                ) : (
                  <>Importar {diarioPreview.n_cuentas.toLocaleString("es-ES")} cuentas<ArrowRight className="h-4 w-4 ml-2" /></>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─────────────── PASO 3 (modo diario Holded): RESULTADO ─────────────── */}
      {step === 3 && modoDiario && diarioResult && (
        <Card data-testid="resultado-diario-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              Diario de {diarioResult.anio} importado
            </CardTitle>
            <CardDescription>
              {diarioResult.stats.total} cuentas ({diarioResult.stats.balance} balance ·{" "}
              {diarioResult.stats.pyg} PyG · {diarioResult.stats.pendientes} pendientes) ·{" "}
              {diarioResult.apuntes_guardados.toLocaleString("es-ES")} apuntes guardados para
              navegar la contabilidad (botón "Contabilidad" en Análisis: sumas y saldos,
              mayores y asientos).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(diarioResult.avisos || []).length > 0 && (
              <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/40">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-700 dark:text-amber-400">Avisos</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc pl-4 space-y-1">
                    {diarioResult.avisos.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
            <div className="flex gap-3">
              <Button variant="outline" onClick={reset}>Importar otro archivo</Button>
              <Button className="flex-1" onClick={handleContinue} data-testid="continuar-diario-btn">
                Continuar<ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ───────────────────────── PASO 2: VALIDAR ───────────────────────── */}
      {step === 2 && preview && mapping && (
        <Card data-testid="validar-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-primary" />
              Esto es lo que he detectado
            </CardTitle>
            <CardDescription>
              Revisa que cada columna esté bien asignada. Corrige lo que haga falta y, si cambias algo,
              pulsa “Validar de nuevo”.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Selector de fila de encabezado */}
            <div className="space-y-1.5">
              <Label>Fila de encabezado (dónde están los títulos de columna)</Label>
              <Select value={String(mapping.fila_encabezado ?? 0)} onValueChange={handleHeaderChange}>
                <SelectTrigger data-testid="sel-encabezado"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {filasCrudas.map((celdas, idx) => (
                    <SelectItem key={idx} value={String(idx)}>{filaLabel(celdas, idx)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Detectado en la <strong>fila {(preview.encabezado?.fila_encabezado ?? 0) + 1}</strong>
                {" · "}{preview.columnas?.length} columnas{" · "}{preview.n_cuentas} cuentas de detalle
              </p>
            </div>

            {/* Asignación de columnas */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Columna de Código contable</Label>
                <Select value={mapping.col_codigo} onValueChange={(v) => setM({ col_codigo: v })}>
                  <SelectTrigger data-testid="sel-codigo"><SelectValue placeholder="Elegir columna" /></SelectTrigger>
                  <SelectContent>
                    {columnas.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Columna de Descripción</Label>
                <Select value={mapping.col_nombre} onValueChange={(v) => setM({ col_nombre: v })}>
                  <SelectTrigger data-testid="sel-nombre"><SelectValue placeholder="Elegir columna" /></SelectTrigger>
                  <SelectContent>
                    {columnas.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Modo de importe (excluyente) */}
            <div className="space-y-2">
              <Label>Importe — ¿cómo viene en tu fichero?</Label>
              <RadioGroup
                value={mapping.modo_saldo}
                onValueChange={(v) => setM({ modo_saldo: v })}
                className="flex flex-wrap gap-4"
              >
                {MODOS.map((m) => (
                  <div key={m.value} className="flex items-center gap-2">
                    <RadioGroupItem value={m.value} id={`modo-${m.value}`} />
                    <Label htmlFor={`modo-${m.value}`} className="cursor-pointer font-normal">{m.label}</Label>
                  </div>
                ))}
              </RadioGroup>
              <div className="grid sm:grid-cols-2 gap-4 pt-1">
                {modoActual?.cols.map((colKey, i) => (
                  <div key={colKey} className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">{modoActual.titulos[i]}</Label>
                    <Select value={mapping[colKey]} onValueChange={(v) => setM({ [colKey]: v })}>
                      <SelectTrigger data-testid={`sel-${colKey}`}><SelectValue placeholder="Elegir columna" /></SelectTrigger>
                      <SelectContent>
                        {columnas.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Button variant="outline" size="sm" onClick={() => handleRevalidar()} disabled={analyzing}>
                {analyzing ? "Validando..." : "Validar de nuevo"}
              </Button>
            </div>

            {/* Jerarquía: subtotales vs cuentas de detalle */}
            {jq?.tiene_jerarquia && (
              <Alert className="border-blue-300 bg-blue-50 dark:bg-blue-950/40">
                <Layers className="h-4 w-4 text-blue-600" />
                <AlertTitle className="text-blue-700 dark:text-blue-400">
                  El fichero trae niveles de agregación (grupos/subtotales)
                </AlertTitle>
                <AlertDescription className="space-y-2">
                  <p>
                    He detectado <strong>{jq.n_subtotales}</strong> filas de subtotal (códigos que son
                    agregación de otras) y <strong>{jq.n_detalle}</strong> cuentas de detalle. Sumar todo
                    contaría el mismo importe varias veces.
                  </p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={!!mapping.excluir_subtotales}
                      onCheckedChange={handleToggleSubtotales}
                      data-testid="chk-subtotales"
                    />
                    <span className="text-sm">Importar solo las cuentas de detalle (recomendado)</span>
                  </label>
                </AlertDescription>
              </Alert>
            )}

            {/* Verja anti-cierre: si el fichero trae el asiento de cierre, cuadra a
                cero "por el motivo malo" (todo saldado) y el PyG/balance saldría
                vacío. Este aviso manda sobre el chivato verde. */}
            {preview.aviso_cierre && (
              <Alert className="border-red-300 bg-red-50 dark:bg-red-950/40" data-testid="aviso-cierre">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertTitle className="text-red-700 dark:text-red-400">
                  Este fichero incluye el asiento de cierre del ejercicio
                </AlertTitle>
                <AlertDescription>{preview.aviso_cierre.mensaje}</AlertDescription>
              </Alert>
            )}

            {/* Chivato de integridad */}
            {preview.aviso_cierre ? null : preview.cuadrado ? (
              <Alert className="border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <AlertTitle className="text-emerald-700 dark:text-emerald-400">El fichero cuadra a cero</AlertTitle>
                <AlertDescription>
                  Suma de saldos: <strong>{fmtEur(preview.suma_saldos)}</strong>. Una Suma y Saldos completa
                  cuadra a cero; esto indica que no falta ninguna cuenta.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/40">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-700 dark:text-amber-400">Atención: el fichero no cuadra a cero</AlertTitle>
                <AlertDescription>
                  Suma de saldos: <strong>{fmtEur(preview.suma_saldos)}</strong> (debería ser 0,00 €).
                  Revisa la fila de encabezado, el modo de importe/columnas o si sobra/falta alguna fila.
                </AlertDescription>
              </Alert>
            )}

            {/* Muestra */}
            <div className="space-y-2">
              <Label>Muestra de las primeras filas (así se leerán)</Label>
              <p className="text-xs text-muted-foreground">
                Las marcadas como <em>subtotal</em> no se importan. Puedes excluir manualmente cualquier fila.
              </p>
              <div className="rounded-md border max-h-80 overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead className="w-32">Código</TableHead>
                      <TableHead>Descripción</TableHead>
                      <TableHead className="text-right">Saldo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.muestra?.map((r, i) => {
                      const manualExcl = excluidos.has(r.codigo);
                      const fueraPorSubtotal = r.es_subtotal && mapping.excluir_subtotales;
                      const fuera = manualExcl || fueraPorSubtotal;
                      return (
                        <TableRow key={`${r.codigo}-${i}`} className={fuera ? "opacity-45" : ""}>
                          <TableCell>
                            {!fueraPorSubtotal && (
                              <Checkbox
                                checked={!manualExcl}
                                onCheckedChange={(c) => handleToggleExcluirCodigo(r.codigo, !c)}
                                title={manualExcl ? "Incluir esta cuenta" : "Excluir esta cuenta"}
                              />
                            )}
                          </TableCell>
                          <TableCell className="font-mono">
                            {r.codigo}
                            {r.es_subtotal && (
                              <Badge variant="secondary" className="ml-2 text-[11px] py-0">subtotal</Badge>
                            )}
                          </TableCell>
                          <TableCell className={fuera ? "line-through" : ""}>{r.nombre}</TableCell>
                          <TableCell className="text-right font-mono">{fmtEur(r.saldo)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Acciones */}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={reset}>
                <ArrowLeft className="h-4 w-4 mr-2" />Cambiar archivo
              </Button>
              <Button className="flex-1" onClick={handleImport} disabled={importing || !mappingValido(mapping)} data-testid="importar-btn">
                {importing ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-2 border-background border-t-transparent mr-2" />Importando...</>
                ) : (
                  <>Importar {preview.n_cuentas} cuentas<ArrowRight className="h-4 w-4 ml-2" /></>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ───────────────────────── PASO 3: RESULTADO ───────────────────────── */}
      {step === 3 && result && (
        <Card className="animate-fade-in" data-testid="result-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {result.integridad_ok ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-600" />
              )}
              Importación Completada
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-muted">
                <p className="text-sm text-muted-foreground">Total cuentas</p>
                <p className="text-2xl font-mono font-bold">{result.stats.total}</p>
              </div>
              <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950">
                <p className="text-sm text-emerald-600 dark:text-emerald-400">Balance</p>
                <p className="text-2xl font-mono font-bold text-emerald-600 dark:text-emerald-400">{result.stats.balance}</p>
              </div>
              <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950">
                <p className="text-sm text-blue-600 dark:text-blue-400">PyG</p>
                <p className="text-2xl font-mono font-bold text-blue-600 dark:text-blue-400">{result.stats.pyg}</p>
              </div>
              <div className={`p-4 rounded-lg ${result.stats.pendientes > 0 ? "bg-amber-50 dark:bg-amber-950" : "bg-muted"}`}>
                <p className={`text-sm ${result.stats.pendientes > 0 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`}>Pendientes</p>
                <p className={`text-2xl font-mono font-bold ${result.stats.pendientes > 0 ? "text-amber-600 dark:text-amber-400" : ""}`}>{result.stats.pendientes}</p>
              </div>
            </div>

            {/* Integridad suma-cero + subtotales excluidos */}
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">Suma del fichero:</span>
              <Badge variant={result.cuadrado ? "default" : "destructive"} className="font-mono">
                {fmtEur(result.suma_saldos)} {result.cuadrado ? "· cuadra ✓" : "· no cuadra"}
              </Badge>
              {result.stats.subtotales_excluidos > 0 && (
                <Badge variant="secondary" className="font-mono">
                  {result.stats.subtotales_excluidos} subtotales excluidos
                </Badge>
              )}
            </div>

            {/* Avisos */}
            {(result.avisos || []).length > 0 && (
              <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/40">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-700 dark:text-amber-400">Avisos</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc pl-4 space-y-1">
                    {result.avisos.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Clasificación automática</span>
                <span className="font-mono">
                  {result.stats.total > 0 ? Math.round(((result.stats.balance + result.stats.pyg) / result.stats.total) * 100) : 0}%
                </span>
              </div>
              <Progress value={result.stats.total > 0 ? ((result.stats.balance + result.stats.pyg) / result.stats.total) * 100 : 0} />
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={reset}>Importar Otro</Button>
              <Button className="flex-1" onClick={handleContinue} data-testid="continue-btn">
                {result.stats.pendientes > 0 ? "Revisar Pendientes" : "Ver Dashboard"}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
